<script lang="ts">
    
    import { getContext, onMount } from "svelte";
    import { navigate } from "svelte-navigator";
    import { ButtonGroup, FormGroup, Icon } from "sveltestrap";
    import ControlLabel from "../../Components/ControlLabel.svelte";
    import FormFeedback from "../../Components/FormFeedback.svelte";
    import Button from "../../Components/Manager/Button.svelte";
    import Menu from "../../Components/Manager/Menu.svelte";
    import TopBar from "../../Components/Shared/Mobile/TopBar.svelte";
    import type { CheckerName, GameType } from "../../global";
    import type { Account } from "../../Model/Account";
    import type { Game } from "../../Model/Game";
    import type { Room } from "../../Model/Room";
    import { accountsService, authorizationService, gamesService, meService, monitorScreenService, realTimeService, roomsService, uuidGenerator } from "../../Services";
    import { i18n } from "../../Services/i18n";
    import RightMenuBtn from "./RightMenuBtn.svelte";

    type RoomAndState = {
        room: Room;
        state: string;
    };

    const statesNames = {
        waiting: 'ESPERANDO',
        playing: 'JUGANDO',
        available: 'DISPONIBLE',
    };

    const setGame: (game: Game) => void = getContext('setGame');

    let currentAcount: Account;
    let creatingGame = false;
    let roomsStates: RoomAndState[] = [];
    let roomId: string;
    let waitTime: number;
    let gameType: GameType = 'Bingo90';
    let openMenu = false;
    let useLineChecker = true;
    let useColumnChecker = false;
    let useDiagonalChecker = false;
    let useCornersChecker = false;
    let playWithPaper = false;

    onMount(init);

    function setDefaultCheckers(): void {
        useLineChecker = gameType === 'Bingo90';
        useColumnChecker = false;
        useDiagonalChecker = false;
        useCornersChecker = false;
    }

    function setAllCheckers(): void {
        useLineChecker = true;
        useColumnChecker = true;
        useDiagonalChecker = true;
        useCornersChecker = true;
    }

    function setNoCheckers(): void {
        useLineChecker = false;
        useColumnChecker = false;
        useDiagonalChecker = false;
        useCornersChecker = false;
    }

    function onGameTypeChange(): void {
        setDefaultCheckers();
    }

    async function init(): Promise<void> {
        try {

            const me = await meService.getMePromise();

            const [account, rooms, states] = await Promise.all([
                accountsService.getAccount(me.accountId),
                roomsService.getRooms(),
                roomsService.getRoomsStates()
            ]);

            currentAcount = account;
            
            roomsStates = rooms.map(room => ({
                room,
                state: states[room.id]
            }));
        } catch (error) {
            console.error(error);
        }
    }

    async function onCreate(): Promise<void> {
        if (creatingGame) {
            return;
        }
        try {
            creatingGame = true;
            const gameId = uuidGenerator.createUuid();

            const useCheckers: CheckerName[] = [];

            if (useLineChecker) useCheckers.push('line');
            if (useColumnChecker) useCheckers.push('column');
            if (useDiagonalChecker) useCheckers.push('diagonal');
            if (useCornersChecker) useCheckers.push('corners');

            setGame(await gamesService.createNewGame(gameId, roomId, waitTime, gameType, useCheckers, playWithPaper));
            realTimeService.start(gameId, roomId);
            localStorage.setItem('gameStart', new Date().toISOString());
        } catch (error) {
            console.error(error);
        } finally {
            creatingGame = false;
        }
    }

    function toggleMenu(): void {
        openMenu = !openMenu;
    }

    function onLogout(): void {
        if (!confirm('¿Seguro que desea salir?')) {
            return;
        }
        authorizationService.clearToken();
        navigate('/login');
    }

    function openMonitor(): void {
        if (!roomId) {
            return;
        }
        monitorScreenService.openMonitor(roomId);
    }

</script>

<TopBar>
    <RightMenuBtn on:click={toggleMenu} />
</TopBar>

<div class="content notranslate">
    <FormGroup>
        <FormFeedback>
            <ControlLabel required>{$i18n("Seleccione una sala")}</ControlLabel>
            <div class="input-group">
                <select class="form-select" bind:value={roomId}>
                    <option value="">-</option>
                    {#each roomsStates as { room, state }}
                        <option
                            value={room.id}
                            disabled={state !== 'available'}>
                            {room.name.value.toUpperCase()} (MODO {room.integratedDisplay.value ? 'PISCINA' : 'TEATRO'}) - {statesNames[state]}
                        </option>
                    {/each}
                </select>
                <button class="btn btn-primary" type="button" on:click={openMonitor} disabled={!roomId}>
                    <Icon name="tv" /> MONITOR
                </button>
            </div>
        </FormFeedback>
    </FormGroup>
    <FormGroup>
        <FormFeedback>
            <ControlLabel required>{$i18n("Tiempo de espera")}</ControlLabel>
            <select class="form-select" bind:value={waitTime}>
                <option value={10}>10 {$i18n("MINUTOS")}</option>
                <option value={5}>5 {$i18n("MINUTOS")}</option>
                <option value={1}>1 {$i18n("MINUTO")}</option>
            </select>
        </FormFeedback>
    </FormGroup>

    {#if currentAcount}
        <FormGroup>
            <FormFeedback>
                <ControlLabel required>{$i18n("Tipo de juego")}</ControlLabel>
                <select class="form-select" bind:value={gameType} on:change={onGameTypeChange}>
                    {#if currentAcount.allowedGames.bingo90.value}
                        <option value="Bingo90">BINFUN 90</option>
                    {/if}
                    {#if currentAcount.allowedGames.bingo75.value}
                        <option value="Bingo75">BINFUN 75</option>
                    {/if}
                    {#if currentAcount.allowedGames.bingoMex.value}
                        <option value="BingoMex">LOTERÍA MEXICANA</option>
                    {/if}
                    {#if currentAcount.allowedGames.bingoAnimals.value}
                        <option value="BingoAnimals">BINFUN ANIMALES</option>
                    {/if}
                    {#if currentAcount.allowedGames.bingoCartoon.value}
                        <option value="BingoCartoon">BINFUN CARICATURAS</option>
                    {/if}
                    {#if currentAcount.allowedGames.movies.value}
                        <option value="Movies">BINFUN CINE</option>
                    {/if}
                    {#if currentAcount.allowedGames.moviesKids.value}
                        <option value="MoviesKids">BINFUN CINE KIDS</option>
                    {/if}
                    {#if currentAcount.allowedGames.xmas.value}
                        <option value="XMas">BINFUN X-MAS</option>
                    {/if}
                    {#if currentAcount.allowedGames.football.value}
                        <option value="Football">BINFUN FOOTBALL</option>
                    {/if}
                    {#if currentAcount.allowedGames.halloween.value}
                        <option value="Halloween">BINFUN HALLOWEEN</option>
                    {/if}
                    {#if currentAcount.allowedGames.superheroes.value}
                        <option value="Superheroes">BINFUN SUPERHEROES</option>
                    {/if}
                    {#if currentAcount.allowedGames.cartoon.value}
                        <option value="Cartoon">BINFUN CARTOON</option>
                    {/if}
                </select>
            </FormFeedback>
        </FormGroup>
    {/if}

    <!-- <FormGroup>
        <FormFeedback>
            <div class="mb-2">
                <label><input type="checkbox" bind:checked={playWithPaper} /><ControlLabel>{$i18n("Jugar con papel")}</ControlLabel></label>
            </div>
        </FormFeedback>
    </FormGroup> -->

    {#if gameType === 'Bingo75'}
        <FormGroup>
            <FormFeedback>
                <ControlLabel>{$i18n("Figuras con premio")}</ControlLabel><br>
                <ButtonGroup class="mb-3">
                    <button class="btn btn-primary btn-sm" type="button" on:click={setAllCheckers}>{$i18n("Todas")}</button>
                    <button class="btn btn-primary btn-sm" type="button" on:click={setNoCheckers}>{$i18n("Ninguna")}</button>
                </ButtonGroup>
                <div class="mb-2">
                    <label><input type="checkbox" bind:checked={useLineChecker} /> {$i18n("Línea")}</label>
                </div>
                <div class="mb-2">
                    <label><input type="checkbox" bind:checked={useColumnChecker} /> {$i18n("Columna")}</label>
                </div>
                <div class="mb-2">
                    <label><input type="checkbox" bind:checked={useDiagonalChecker} /> {$i18n("Diagonal")}</label>
                </div>
                <div class="mb-2">
                    <label><input type="checkbox" bind:checked={useCornersChecker} /> {$i18n("Esquinas")}</label>
                </div> 
            </FormFeedback>
        </FormGroup>
    {:else if gameType == 'Bingo90'}
        <FormGroup>
            <FormFeedback>
                <ControlLabel>{$i18n("Premiar línea")}</ControlLabel><br>
                <div class="mb-2">
                    <label><input type="checkbox" bind:checked={useLineChecker} /><ControlLabel>{$i18n("Línea")}</ControlLabel></label>
                </div>
            </FormFeedback>
        </FormGroup>
    {/if}
    
    <div class="f-center">
        <Button on:click={onCreate}>{$i18n("Comenzar")}</Button>
    </div>
    
</div>

<Menu isOpen={openMenu} toggle={toggleMenu} on:logout={onLogout} />

<style lang="scss">

    .content {
        padding: 1rem;
        text-transform: uppercase;
    }

</style>